import React from 'react'

import api from '@disco/api/server'
import { useTranslation } from '@disco/i18n/use-translation'
import { SearchSelectDialog } from '@disco/shared-components/ui/search-select-dialog'
import { clientLibraryFeaturedTracksAlter } from '@disco/state-management/actions/client-library-track-actions'
import { loadBatch } from '@disco/state-management/actions/PlaylistTrackActions'
import { pushUINotification } from '@disco/utils/notifications'

import type { ClientLibraryId } from 'types/client-library'
import type { Track, TrackId } from 'types/track'

import TrackInfo from 'components/common/track-info'
import { Text } from 'components/ds'
import { HoverProvider } from 'components/ui/hover-provider'

import { useDispatch } from 'hooks/use-redux'

const RESULTS_COUNT = 20

type Props = {
  libraryId: ClientLibraryId
  excludeIds: TrackId[]
  remaining: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Dialog for searching the whole business library and adding tracks to an artist profile's top tracks.
 * @param libraryId - ID of the artist profile catalog
 * @param excludeIds - Track IDs that are already top tracks
 * @param remaining - How many more tracks can be selected
 * @param open - Whether the dialog is open
 * @param onOpenChange - Called when the dialog opens or closes
 */
const AddTopTracksDialog = ({
  libraryId,
  excludeIds,
  remaining,
  open,
  onOpenChange,
}: Props) => {
  const { t } = useTranslation('catalogs-management')
  const dispatch = useDispatch()

  const searchTracks = async (query: string, signal: AbortSignal) => {
    const response = await api.getSearchResultsByQuery(
      undefined,
      {
        count: RESULTS_COUNT,
        exclude_track_ids: excludeIds.join(',') || undefined,
        q: query,
        sort: '["-score"]',
        type: 'track',
      },
      undefined,
      signal,
    )
    const tracks: Track[] = Array.isArray(response.results)
      ? response.results
      : []
    dispatch(loadBatch(tracks))
    return tracks
  }

  const addTracks = async (selected: Track[]) => {
    const { skippedTrackIds }: { skippedTrackIds: TrackId[] } = await dispatch(
      clientLibraryFeaturedTracksAlter(
        libraryId,
        selected.map(track => track.id),
        [],
      ),
    ).catch((error: unknown) => {
      pushUINotification({
        message: t(
          'catalogs-management:topTracksCouldNotBeUpdated',
          'Tracks could not be updated, please try again.',
        ),
        level: 'error',
      })
      // Rejecting keeps the dialog open with the selection, ready to retry
      throw error
    })

    if (skippedTrackIds.length > 0) {
      pushUINotification({
        message: t(
          'catalogs-management:someTracksCouldNotBeAdded',
          '{{skipped}} of {{count}} tracks couldn’t be added. Check your catalog track quota and included formats.',
          {
            count: selected.length,
            skipped: skippedTrackIds.length,
            defaultValue_one:
              'This track couldn’t be added. Check your catalog track quota and included formats.',
          },
        ),
        level: 'warning',
      })
    } else {
      pushUINotification({
        message: t('catalogs-management:topTracksUpdated', 'Tracks updated!'),
        level: 'success',
      })
    }
  }

  return (
    <SearchSelectDialog<Track>
      open={open}
      onOpenChange={onOpenChange}
      title={t('catalogs-management:addTopTracks', 'Add top tracks')}
      search={searchTracks}
      getKey={track => track.id}
      getItemLabel={track => track.name}
      renderItem={track => (
        <HoverProvider>
          <TrackInfo location="explorer" track={track} />
        </HoverProvider>
      )}
      renderSelectedItem={track => (
        <Text as="p" size="2" truncate>
          {track.name}
          {track.artist && (
            <Text size="1" color="gray">
              {' – '}
              {track.artist}
            </Text>
          )}
        </Text>
      )}
      onConfirm={addTracks}
      confirmLabel={count =>
        t('catalogs-management:addCountTracks', 'Add {{count}} tracks', {
          count,
          defaultValue_one: 'Add {{count}} track',
        })
      }
      max={remaining}
      placeholder={t('catalogs-management:searchTracks', 'Search tracks')}
      emptyQueryMessage={t(
        'catalogs-management:searchYourLibraryForTracks',
        'Search your library for tracks to add',
      )}
    />
  )
}

export default AddTopTracksDialog
